import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Loader2, FileSpreadsheet, CheckCircle2, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

function buildQueryBusiness(selectFields: string) {
  return supabase
    .from("mailing_records")
    .select(selectFields)
    .eq("times_mailed", 0)
    .not("business_name", "is", null)
    .neq("business_name", "")
    .eq("mailing_status", "active")
    .order("business_name", { ascending: true })
    .order("last_name", { ascending: true });
}

export function BusinessMailingExport() {
  const [exporting, setExporting] = useState(false);
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [mailDate, setMailDate] = useState(new Date().toISOString().split("T")[0]);
  const [committing, setCommitting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      let allRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQueryBusiness(
          "first_name, last_name, business_name, street_address, city, state, zip_code, county"
        ).range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No business records found matching criteria");
        setExporting(false);
        return;
      }

      const rows = allRecords.map((r) => ({
        "First Name": r.first_name || "",
        "Last Name": r.last_name || "",
        "Business Name": r.business_name || "",
        "Street Address": r.street_address || "",
        "City": r.city || "",
        "State": (r.state || "").toUpperCase(),
        "Zip Code": (r.zip_code || "").split("-")[0],
        "County": r.county || "",
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 30 },
        { wch: 18 }, { wch: 8 }, { wch: 12 }, { wch: 16 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Business Records");
      XLSX.writeFile(wb, `Business_Never_Mailed_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} business records to Excel`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleCommitToMail = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    setCommitting(true);
    try {
      let allIds: string[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQueryBusiness("id, times_mailed")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allIds = [...allIds, ...data.map((r: any) => r.id)];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allIds.length === 0) {
        toast.error("No business records match criteria");
        setCommitting(false);
        return;
      }

      const batchSize = 500;
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);

        const historyEntries = batch.map((id) => ({
          mailing_record_id: id,
          campaign_name: campaignName.trim(),
          mailed_date: mailDate,
          mailing_round: 1,
        }));

        const { error: historyError } = await supabase
          .from("mailing_history")
          .insert(historyEntries);
        if (historyError) throw historyError;

        for (const id of batch) {
          const { error: updateError } = await supabase
            .from("mailing_records")
            .update({
              times_mailed: (await supabase.from("mailing_records").select("times_mailed").eq("id", id).single()).data?.times_mailed + 1 || 1,
              last_mailed_date: mailDate,
            })
            .eq("id", id);
          if (updateError) throw updateError;
        }
      }

      toast.success(`Committed ${allIds.length} business records to "${campaignName.trim()}"`);
      setCommitDialogOpen(false);
      setCampaignName("");
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <>
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Business Entities — Never Mailed
          </CardTitle>
          <CardDescription>
            Records with a business entity name that have never been mailed. Export includes First Name, Last Name, and Business Name columns.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 items-center flex-wrap">
            <Button onClick={handleExport} disabled={exporting} className="gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Exporting..." : "Download Business Excel"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCommitDialogOpen(true)}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Commit to Mail
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={commitDialogOpen} onOpenChange={setCommitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit Business Records to Mail</DialogTitle>
            <DialogDescription>
              This will mark matching never-mailed business entity records as mailed, increment their mail count, and create history entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. March 2026 – Business Entities"
              />
            </div>
            <div>
              <Label>Mail Date</Label>
              <Input
                type="date"
                value={mailDate}
                onChange={(e) => setMailDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCommitToMail} disabled={committing} className="gap-2">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {committing ? "Committing..." : "Commit Records"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
